import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  Loading,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "../../../../components";
import { InputErros, value } from "../../../../helpers";
import { relationListCompra, relationListVenda } from "./constantes";
import shortid from "shortid";
import itemParametroPrecificacaoValidator from "../../middlewares/item-parametro-precificacao.validator";
import { notification, ParametrosAliquotaService } from "../../../../services";
import { responseErros } from "../../../../utils";
import { DialogActions, Popover } from "@mui/material";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

const VAR_BUTTON_STYLES = {
  borderRadius: "16px",
  textTransform: "none",
  fontSize: "0.75rem",
  padding: "4px 12px",
  borderColor: "#ddd",
  color: "#555",
  "&:hover": {
    backgroundColor: "#f5f5f5",
    borderColor: "#ccc",
  },
};

const TOGGLE_BUTTON_STYLES = {
  ...VAR_BUTTON_STYLES,
  backgroundColor: "#b5deff",
  color: "#16528f",
  "&:hover": {
    backgroundColor: "#dcefff",
    borderColor: "#ccc",
  },
};

export function ParametroPrecificacaoDialog({ open, setOpen }) {
  const [parametroPrecificacaoListCompra, setParametroPrecificacaoListCompra] =
    useState([]);
  const [parametroPrecificacaoListVenda, setParametroPrecificacaoListVenda] =
    useState([]);
  const [idsRemoved, setIdsRemoved] = useState([]);
  const [focoInfoIndex, setFocoInfoIndex] = useState(null);
  const [showAllVariables, setShowAllVariables] = useState(false);
  const [popoverState, setPopoverState] = useState({
    anchorEl: null,
    index: null,
  });
  const [loading, setLoading] = useState(false);
  const [inputErro, setInputErro] = useState([]);
  const [palavrasChaves, setPalavrasChaves] = useState([
    { value: "PrRef", label: "Pr. Referência" },
    { value: "Parametro", label: "Parâmetro" },
  ]);
  const inputErros = new InputErros(inputErro, setInputErro);
  const inputRef = useRef([]);

  const parametrosAliquotaService = new ParametrosAliquotaService();

  const operacoesChaves = [
    { value: "(", label: "(", operadorMatematico: false, icon: "(" },
    { value: ")", label: ")", operadorMatematico: false, icon: ")" },
    {
      value: "+",
      label: "+",
      operadorMatematico: true,
      icon: "ph-bold ph-plus",
    },
    {
      value: "-",
      label: "-",
      operadorMatematico: true,
      icon: "ph-bold ph-minus",
    },
    { value: "*", label: "*", operadorMatematico: true, icon: "ph-bold ph-x" },
    {
      value: "/",
      label: "/",
      operadorMatematico: true,
      icon: "ph-bold ph-divide",
    },
  ];

  useEffect(() => {
    if (open) {
      buscarParametroPrecificacao();
    }
  }, [open]);

  function escapeRegExp(string = "") {
    return String(string).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  async function buscarParametroPrecificacao(filtros = {}) {
    setLoading(true);
    const res = await parametrosAliquotaService.getAll(filtros);
    if (!res.isAxiosError) {
      const compra = res.data.COMPRA || [];
      const venda = res.data.VENDA || [];

      const novos = [
        { value: "PrRef", label: "Pr. Referência" },
        { value: "Parametro", label: "Parâmetro" },
      ];

      const addPalavraChave = (item) => {
        const identificadorLimpo = item.identificador?.startsWith("_")
          ? item.identificador.substring(1)
          : item.identificador;

        if (!novos.find((p) => p.value === identificadorLimpo)) {
          novos.push({ value: identificadorLimpo, label: item.descricao });
        }
      };

      compra.forEach(addPalavraChave);
      venda.forEach(addPalavraChave);

      setPalavrasChaves(novos);

      const converterValuesParaLabelsComMapa = (expressao) => {
        if (!expressao) return "";
        let result = expressao;
        novos.forEach(({ label, value }) => {
          const escapedValue = escapeRegExp(value);
          const regex = new RegExp(
            `(?<![a-zA-Z0-9_])${escapedValue}(?![a-zA-Z0-9_])`,
            "g"
          );
          result = result.replace(regex, label);
        });
        return result;
      };

      setParametroPrecificacaoListCompra(
        compra
          .sort((a, b) => a.ordem - b.ordem)
          .map((item) => ({
            ...item,
            calculoSemParametro: item.calculoSemParametro ?? false,
            expressao: converterValuesParaLabelsComMapa(item.expressao),
          }))
      );

      setParametroPrecificacaoListVenda(
        venda
          .sort((a, b) => a.ordem - b.ordem)
          .map((item) => ({
            ...item,
            calculoSemParametro: item.calculoSemParametro ?? false,
            expressao: converterValuesParaLabelsComMapa(item.expressao),
          }))
      );
    } else {
      responseErros(res);
    }
    setLoading(false);
  }

  function handleDescricaoBlur(index, tipo) {
    const lista =
      tipo === "compra"
        ? parametroPrecificacaoListCompra
        : parametroPrecificacaoListVenda;
    const item = lista?.[index];
    if (!item || !item.identificador) return;

    const identificadorLimpo = item.identificador.startsWith("_")
      ? item.identificador.substring(1)
      : item.identificador;

    const newLabel = (item.descricao || "").trim();
    if (!newLabel) return;

    setPalavrasChaves((prevPalavras) => {
      const existente = prevPalavras.find(
        (p) => p.value === identificadorLimpo
      );

      if (existente) {
        if (existente.label === newLabel) return prevPalavras;

        const oldLabel = existente.label;
        const updatedPalavras = prevPalavras.map((p) =>
          p.value === identificadorLimpo ? { ...p, label: newLabel } : p
        );

        const regexLabel = new RegExp(
          `(?<![a-zA-Z0-9_])${escapeRegExp(oldLabel)}(?![a-zA-Z0-9_])`,
          "g"
        );

        setParametroPrecificacaoListCompra((prev) =>
          prev.map((it) => {
            if (!it.expressao) return it;
            if (it.identificador === item.identificador) {
              const regexSelf = new RegExp(
                `(?<![a-zA-Z0-9_])${escapeRegExp(newLabel)}(?![a-zA-Z0-9_])`,
                "g"
              );
              return { ...it, expressao: it.expressao.replace(regexSelf, "") };
            }
            return it;
          })
        );

        setParametroPrecificacaoListVenda((prev) =>
          prev.map((it) =>
            it.expressao
              ? { ...it, expressao: it.expressao.replace(regexLabel, newLabel) }
              : it
          )
        );

        return updatedPalavras;
      } else {
        const novo = { value: identificadorLimpo, label: newLabel };

        const regexId = new RegExp(
          `(?<![a-zA-Z0-9_])${escapeRegExp(
            identificadorLimpo
          )}(?![a-zA-Z0-9_])`,
          "g"
        );

        setParametroPrecificacaoListCompra((prev) =>
          prev.map((it) =>
            it.expressao
              ? { ...it, expressao: it.expressao.replace(regexId, newLabel) }
              : it
          )
        );
        setParametroPrecificacaoListVenda((prev) =>
          prev.map((it) =>
            it.expressao
              ? { ...it, expressao: it.expressao.replace(regexId, newLabel) }
              : it
          )
        );

        return [...prevPalavras, novo];
      }
    });
  }

  const handleChangeInputCompra = (event, i) => {
    setParametroPrecificacaoListCompra((prevState) => [
      ...prevState.map((obj, index) => {
        if (index == i) {
          const name = event.target.name;
          return {
            ...obj,
            incidencia: "COMPRA",
            tipo: relationListCompra.find(
              (compra) => compra.value == event.target.value
            )?.tipo,
            [name]: event.target.value ? event.target.value : undefined,
          };
        }
        return obj;
      }),
    ]);
  };

  const handleChangeInputVenda = (event, i) => {
    setParametroPrecificacaoListVenda((prevState) => [
      ...prevState.map((obj, index) => {
        if (index == i) {
          const name = event.target.name;
          return {
            ...obj,
            incidencia: "VENDA",
            [name]: event.target.value ? event.target.value : undefined,
          };
        }
        return obj;
      }),
    ]);
  };

  const onClose = () => {
    setIdsRemoved([]);
    setInputErro([]);
    setParametroPrecificacaoListCompra([]);
    setParametroPrecificacaoListVenda([]);
    setOpen(false);
  };

  function onClickAddItem(setState) {
    setState((prevState) => [
      ...prevState,
      {
        id: shortid.generate(),
        identificador: `_${shortid.generate()}`,
        ativado: true,
        calculoSemParametro: false,
      },
    ]);
  }

  async function onSubmit() {
    const itens = {
      compra: parametroPrecificacaoListCompra,
      venda: parametroPrecificacaoListVenda,
    };
    setLoading(true);
    try {
      await itemParametroPrecificacaoValidator.validate(itens, {
        abortEarly: false,
      });
      setInputErro([]);

      const body = {
        parametros: [
          ...parametroPrecificacaoListCompra.map((item, index) => ({
            ...item,
            expressao: converterLabelsParaValues(item.expressao),
            ordem: index + 1,
          })),
          ...parametroPrecificacaoListVenda.map((item, index) => ({
            ...item,
            expressao: converterLabelsParaValues(item.expressao),
            ordem: index + 1,
          })),
        ],
        idsRemoved,
      };
      const res = await parametrosAliquotaService.cadastrar(body);
      if (!res.isAxiosError) {
        notification.sucessoGenericos("Parâmetros salvos com sucesso!");
        onClose();
      } else {
        responseErros(res);
      }
    } catch (err) {
      inputErros.set(err);
    } finally {
      setLoading(false);
    }
  }

  function converterLabelsParaValues(expressao) {
    if (!expressao) return "";
    let result = expressao;
    palavrasChaves.forEach(({ label, value }) => {
      const escapedLabel = escapeRegExp(label);
      const regex = new RegExp(
        `(?<![a-zA-Z0-9_])${escapedLabel}(?![a-zA-Z0-9_])`,
        "g"
      );
      result = result.replace(regex, value);
    });
    return result;
  }

  const inserirVariavelNaExpressao = (label) => {
    if (focoInfoIndex === null) return;
    const input = inputRef.current[focoInfoIndex];
    if (!input) return;

    const parametroAtual = parametroPrecificacaoListCompra[focoInfoIndex];
    if (!parametroAtual) return;

    const identificadorLimpo = parametroAtual.identificador.startsWith("_")
      ? parametroAtual.identificador.substring(1)
      : parametroAtual.identificador;

    const mesmaPalavra = palavrasChaves.find(
      (p) => p.value === identificadorLimpo
    );

    if (mesmaPalavra && mesmaPalavra.label === label) {
      return notification.alertaGenericos(
        "Um parâmetro não pode usar ele mesmo na expressão!"
      );
    }

    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? 0;
    const valorAtual = parametroAtual.expressao || "";

    const antesCursor = valorAtual.substring(0, start).trimEnd();
    const ultimoToken = obterUltimoToken(antesCursor);

    const isPalavraChave = palavrasChaves.some((op) => op.label === label);
    const operacaoEncontrada = operacoesChaves.find((op) => op.value === label);
    const operadorMatematico = operacaoEncontrada?.operadorMatematico ?? false;

    const ultimoTokenEhPalavraChave = palavrasChaves.some(
      (op) => op.label === ultimoToken
    );
    const ultimoTokenEhOperador = operacoesChaves.find(
      (op) => op.value === ultimoToken
    )?.operadorMatematico;

    if (isPalavraChave && ultimoTokenEhPalavraChave) return;
    if (operadorMatematico && ultimoTokenEhOperador) return;

    const novoValor =
      valorAtual.substring(0, start) + label + valorAtual.substring(end);

    const novaLista = [...parametroPrecificacaoListCompra];
    novaLista[focoInfoIndex] = {
      ...novaLista[focoInfoIndex],
      expressao: novoValor,
    };
    setParametroPrecificacaoListCompra(novaLista);

    setTimeout(() => {
      input.focus();
      input.selectionStart = input.selectionEnd = start + label.length;
    }, 0);
  };

  const obterUltimoToken = (texto) => {
    const regex = /([^\s()+\-*/{}]+|[()+\-*/{}])$/;
    const match = texto.match(regex);
    return match ? match[0] : "";
  };

  const setInputRef = useCallback(
    (index) => (el) => {
      inputRef.current[index] = el;
    },
    []
  );

  function onKeyDownExpressao(parametro, event, i) {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      " ",
    ];
    const isNumber = /^[0-9]$/.test(event.key);
    const isOperator = /^[+\-*/()]$/.test(event.key);

    const input = inputRef.current[i];
    const cursorPos = input?.selectionStart ?? 0;
    const expressaoAtual = parametro.expressao || "";

    if (["Backspace", "Delete"].includes(event.key)) {
      for (const palavra of palavrasChaves) {
        const label = palavra.label;
        const escapedLabel = escapeRegExp(label);
        const regex = new RegExp(escapedLabel, "g");
        let match;
        while ((match = regex.exec(expressaoAtual)) !== null) {
          const startIdx = match.index;
          const endIdx = startIdx + label.length;
          if (cursorPos >= startIdx && cursorPos <= endIdx) {
            event.preventDefault();
            const novaExpressao =
              expressaoAtual.slice(0, startIdx) + expressaoAtual.slice(endIdx);

            const novaLista = [...parametroPrecificacaoListCompra];
            novaLista[i] = {
              ...novaLista[i],
              expressao: limparExpressao(novaExpressao),
            };
            setParametroPrecificacaoListCompra(novaLista);

            setTimeout(() => {
              input.focus();
              input.selectionStart = input.selectionEnd = startIdx;
            }, 0);

            return;
          }
        }
      }
    }

    if (!isNumber && !isOperator && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  const limparExpressao = (expressao) => {
    if (!expressao) return "";
    let result = expressao.replace(/\s+/g, " ").trim();
    result = result.replace(/^[+*/-]\s*/, "").replace(/\s*[+*/-]$/, "");
    result = result.replace(/([+*/-])\s*\1+/g, "$1");
    result = result.replace(/\(\s*\)/g, "");
    return result.trim();
  };

  const handleDeletarParametro = async (i, state, tipo) => {
    const parametroExcluido = state[i];
    const confirmacao = await notification.confirmacao(
      "APAGAR PARÂMETRO",
      "Deseja deletar o parâmetro? Ele será removido de todas as expressões que o utilizam."
    );
    if (!confirmacao.isConfirmed) return;
    if (typeof parametroExcluido.id === "number") {
      setIdsRemoved((prev) => [...new Set([...prev, parametroExcluido.id])]);
    }

    const identificadorLimpo = parametroExcluido.identificador.startsWith("_")
      ? parametroExcluido.identificador.substring(1)
      : parametroExcluido.identificador;

    setPalavrasChaves((prev) =>
      prev.filter((p) => p.value !== identificadorLimpo)
    );

    const removerReferencias = (lista) => {
      return lista.map((item) => {
        if (!item.expressao) return item;
        let nova = item.expressao;

        const labelEsc = escapeRegExp(parametroExcluido.descricao || "");
        const idEsc = escapeRegExp(identificadorLimpo || "");

        if (labelEsc) {
          const regexLabel = new RegExp(
            `(?<![a-zA-Z0-9_])${labelEsc}(?![a-zA-Z0-9_])`,
            "g"
          );
          nova = nova.replace(regexLabel, "");
        }
        if (idEsc) {
          const regexId = new RegExp(
            `(?<![a-zA-Z0-9_])${idEsc}(?![a-zA-Z0-9_])`,
            "g"
          );
          nova = nova.replace(regexId, "");
        }

        nova = limparExpressao(nova);

        return {
          ...item,
          expressao: nova,
        };
      });
    };

    if (tipo === "compra") {
      setParametroPrecificacaoListCompra((prev) =>
        removerReferencias(prev.filter((_, index) => index !== i))
      );
    } else {
      setParametroPrecificacaoListVenda((prev) =>
        removerReferencias(prev.filter((_, index) => index !== i))
      );
    }
  };

  const handleDragEnd = (result, state, setState) => {
    if (!result.destination) return;

    const items = Array.from(state);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setState(items);
  };

  function onClickCheckbox(event, setListaParametros, index) {
    const name = event.target.name,
      value = event.target.checked;
    setListaParametros((prevState) => {
      const listaTemp = [...prevState];
      listaTemp[index] = {
        ...listaTemp[index],
        [name]: value,
      };
      return listaTemp;
    });
  }

  return (
    <Dialog
      fullWidth
      maxWidth="xl"
      PaperComponent="div"
      PaperProps={{
        style: {
          borderRadius: "0.7rem",
          background: `#ffffff`,
        },
      }}
      open={open}
      onClose={() => onClose()}
    >
      <DialogContent>
        <CardHeader
          title="Parametrizar Precificação"
          action={
            <IconButton
              color="inherit"
              onClick={() => onClose()}
              sx={{
                backgroundColor: "#EDEEF0",
                "&:hover": { backgroundColor: "#B6BABC" },
              }}
            >
              <i
                className="ph-bold ph-x"
                style={{ fontSize: "clamp(17px, 4vw, 20px)" }}
              ></i>
            </IconButton>
          }
        />
        <CardContent>
          <Grid container className="mb-4">
            <Grid container spacing={2}>
              <Grid item xs={11.5}>
                <p>
                  <li>
                    Incidência na <b>(Compra)</b>
                  </li>
                </p>
              </Grid>
              {!parametroPrecificacaoListCompra.length ? (
                <Grid item xs={0.5} className="d-flex align-items-center">
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      height: "35px",
                      borderRadius: "50%",
                      minWidth: "15px",
                      boxShadow: "none",
                    }}
                    onClick={() =>
                      onClickAddItem(setParametroPrecificacaoListCompra)
                    }
                  >
                    <i
                      className="ph-bold ph-plus"
                      style={{ color: "#fff", fontSize: 15 }}
                    ></i>
                  </Button>
                </Grid>
              ) : null}
            </Grid>
            <DragDropContext
              onDragEnd={(result) =>
                handleDragEnd(
                  result,
                  parametroPrecificacaoListCompra,
                  setParametroPrecificacaoListCompra
                )
              }
            >
              <Droppable droppableId="parametros-compra">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="w-100"
                  >
                    {parametroPrecificacaoListCompra.map((parametro, i) => (
                      <Draggable key={i} draggableId={String(i)} index={i}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Grid container spacing={2}>
                              <Grid
                                item
                                xs={0.3}
                                className="d-flex align-items-center"
                              >
                                <i
                                  className="ph-bold ph-dots-six-vertical"
                                  style={{ fontSize: 20, color: "#555" }}
                                ></i>
                              </Grid>
                              <Grid item xs={2.7}>
                                <TextField
                                  id="descricao"
                                  name="descricao"
                                  label="Descricao"
                                  variant="outlined"
                                  required
                                  margin="normal"
                                  error={
                                    inputErros.get(`compra[${i}].descricao`) ||
                                    false
                                  }
                                  value={parametro.descricao ?? ""}
                                  onChange={(e) =>
                                    handleChangeInputCompra(e, i)
                                  }
                                  onBlur={() =>
                                    handleDescricaoBlur(i, "compra")
                                  }
                                  type="text"
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={3}>
                                <Select
                                  id={`referencia-${i}`}
                                  name="referencia"
                                  label="Referência"
                                  margin="normal"
                                  error={
                                    inputErros.get(`compra[${i}].referencia`) ||
                                    false
                                  }
                                  value={value.text(parametro?.referencia)}
                                  onChange={(e) =>
                                    handleChangeInputCompra(e, i)
                                  }
                                >
                                  {relationListCompra.map(
                                    ({ label, value }) => (
                                      <MenuItem key={value} value={value}>
                                        {label}
                                      </MenuItem>
                                    )
                                  )}
                                </Select>
                              </Grid>
                              <Grid
                                item
                                xs={
                                  i ===
                                  parametroPrecificacaoListCompra.length - 1
                                    ? 4.5
                                    : 5
                                }
                              >
                                <TextField
                                  inputRef={setInputRef(i)}
                                  id="expressao"
                                  name="expressao"
                                  label="Expressão"
                                  variant="outlined"
                                  required
                                  margin="normal"
                                  error={
                                    inputErros.get(`compra[${i}].expressao`) ||
                                    false
                                  }
                                  value={
                                    parametro.expressao
                                      ? parametro.expressao
                                      : ""
                                  }
                                  onChange={(e) =>
                                    handleChangeInputCompra(e, i)
                                  }
                                  onKeyDown={(e) =>
                                    onKeyDownExpressao(parametro, e, i)
                                  }
                                  onFocus={() => setFocoInfoIndex(i)}
                                  type="text"
                                  fullWidth
                                />
                              </Grid>
                              <Grid
                                item
                                xs={0.5}
                                className="d-flex align-items-center mt-2"
                              >
                                <Button
                                  variant="filled"
                                  size="small"
                                  color="primary"
                                  sx={{
                                    height: "40px",
                                    borderRadius: "50%",
                                    minWidth: "15px",
                                    boxShadow: "none",
                                  }}
                                  onClick={(e) =>
                                    setPopoverState({
                                      anchorEl: e.currentTarget,
                                      index: i,
                                    })
                                  }
                                >
                                  <i
                                    className="ph-bold ph-list-plus"
                                    style={{ color: "#8cbffa", fontSize: 20 }}
                                  ></i>
                                </Button>
                                <Popover
                                  anchorEl={popoverState.anchorEl}
                                  open={
                                    popoverState.anchorEl &&
                                    popoverState.index === i
                                  }
                                  onClose={() =>
                                    setPopoverState({
                                      anchorEl: null,
                                      index: null,
                                    })
                                  }
                                  anchorOrigin={{
                                    vertical: "center",
                                    horizontal: "left",
                                  }}
                                  transformOrigin={{
                                    vertical: "center",
                                    horizontal: "right",
                                  }}
                                  elevation={4}
                                  slotProps={{
                                    paper: {
                                      sx: {
                                        borderRadius: 4,
                                        display: "flex",
                                        alignItems: "center",
                                        flexDirection: "row",
                                        gap: 2,
                                        p: 2,
                                      },
                                    },
                                  }}
                                >
                                  <FormControlLabel
                                    label="Remover Incidência Direta"
                                    control={
                                      <Checkbox
                                        name="removerIncidenciaDireta"
                                        checked={
                                          parametro?.removerIncidenciaDireta ??
                                          false
                                        }
                                        onChange={(event) =>
                                          onClickCheckbox(
                                            event,
                                            setParametroPrecificacaoListCompra,
                                            i
                                          )
                                        }
                                        color="primary"
                                      />
                                    }
                                  />
                                  {parametro.tipo !== "DECIMAL" && (
                                    <FormControlLabel
                                      label="Realiza Cálculo Automático"
                                      control={
                                        <Checkbox
                                          name="calculoSemParametro"
                                          checked={
                                            parametro?.calculoSemParametro ??
                                            false
                                          }
                                          onChange={(event) =>
                                            onClickCheckbox(
                                              event,
                                              setParametroPrecificacaoListCompra,
                                              i
                                            )
                                          }
                                          color="primary"
                                        />
                                      }
                                    />
                                  )}
                                </Popover>
                              </Grid>
                              <Grid
                                item
                                xs={0.5}
                                className="d-flex align-items-center mt-2"
                              >
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="error"
                                  sx={{
                                    height: "35px",
                                    borderRadius: "50%",
                                    minWidth: "15px",
                                    boxShadow: "none",
                                  }}
                                  onClick={() =>
                                    handleDeletarParametro(
                                      i,
                                      parametroPrecificacaoListCompra,
                                      "compra"
                                    )
                                  }
                                >
                                  <i
                                    className="ph-bold ph-trash"
                                    style={{ color: "#ffffff", fontSize: 15 }}
                                  ></i>
                                </Button>
                              </Grid>
                              {i ===
                              parametroPrecificacaoListCompra.length - 1 ? (
                                <Grid
                                  item
                                  xs={0.5}
                                  className="d-flex align-items-center mt-2"
                                >
                                  <Button
                                    variant="contained"
                                    size="small"
                                    sx={{
                                      height: "35px",
                                      borderRadius: "50%",
                                      minWidth: "15px",
                                      boxShadow: "none",
                                    }}
                                    onClick={() =>
                                      onClickAddItem(
                                        setParametroPrecificacaoListCompra
                                      )
                                    }
                                  >
                                    <i
                                      className="ph-bold ph-plus"
                                      style={{ color: "#fff", fontSize: 15 }}
                                    ></i>
                                  </Button>
                                </Grid>
                              ) : null}
                            </Grid>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Grid>
          <Grid container className="mt-2">
            <Grid container spacing={2}>
              <Grid item xs={11.5}>
                <p>
                  <li>
                    Incidência na <b>(Venda)</b>
                  </li>
                </p>
              </Grid>
              {!parametroPrecificacaoListVenda.length ? (
                <Grid item xs={0.5} className="d-flex align-items-center">
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      height: "35px",
                      borderRadius: "50%",
                      minWidth: "15px",
                      boxShadow: "none",
                    }}
                    onClick={() =>
                      onClickAddItem(setParametroPrecificacaoListVenda)
                    }
                  >
                    <i
                      className="ph-bold ph-plus"
                      style={{ color: "#fff", fontSize: 15 }}
                    ></i>
                  </Button>
                </Grid>
              ) : null}
            </Grid>
            <DragDropContext
              onDragEnd={(result) =>
                handleDragEnd(
                  result,
                  parametroPrecificacaoListVenda,
                  setParametroPrecificacaoListVenda
                )
              }
            >
              <Droppable droppableId="parametros-venda">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="w-100"
                  >
                    {parametroPrecificacaoListVenda.map((parametro, i) => (
                      <Draggable key={i} draggableId={String(i)} index={i}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <Grid container spacing={2}>
                              <Grid
                                item
                                xs={0.3}
                                className="d-flex align-items-center"
                              >
                                <i
                                  className="ph-bold ph-dots-six-vertical"
                                  style={{ fontSize: 20, color: "#555" }}
                                ></i>
                              </Grid>
                              <Grid item xs={5.7}>
                                <TextField
                                  id="descricao"
                                  name="descricao"
                                  label="Descricao"
                                  variant="outlined"
                                  required
                                  margin="normal"
                                  error={inputErros.get(
                                    `venda[${i}].descricao`
                                  )}
                                  value={
                                    parametro.descricao
                                      ? parametro.descricao
                                      : ""
                                  }
                                  onChange={(e) => handleChangeInputVenda(e, i)}
                                  onBlur={() => handleDescricaoBlur(i, "venda")}
                                  type="text"
                                  fullWidth
                                />
                              </Grid>
                              <Grid
                                item
                                xs={
                                  i ===
                                  parametroPrecificacaoListVenda.length - 1
                                    ? 4.5
                                    : 5
                                }
                              >
                                <Select
                                  id="referencia"
                                  name="referencia"
                                  label="Referência"
                                  margin="normal"
                                  error={
                                    inputErros.get(`venda[${i}].referencia`) ||
                                    false
                                  }
                                  value={value.text(parametro?.referencia)}
                                  onChange={(event) =>
                                    handleChangeInputVenda(event, i)
                                  }
                                >
                                  {relationListVenda.map(({ label, value }) => (
                                    <MenuItem key={value} value={value}>
                                      {label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </Grid>
                              <Grid
                                item
                                xs={0.5}
                                className="d-flex align-items-center mt-2"
                              >
                                <Tooltip
                                  title="Não deduzir diretamente na precificação"
                                  placement="top"
                                >
                                  <Checkbox
                                    name="removerIncidenciaDireta"
                                    checked={
                                      parametro?.removerIncidenciaDireta ??
                                      false
                                    }
                                    onChange={(event) =>
                                      onClickCheckbox(
                                        event,
                                        setParametroPrecificacaoListVenda,
                                        i
                                      )
                                    }
                                    color="primary"
                                  />
                                </Tooltip>
                              </Grid>
                              <Grid
                                item
                                xs={0.5}
                                className="d-flex align-items-center mt-2"
                              >
                                <Button
                                  variant="contained"
                                  size="small"
                                  color="error"
                                  sx={{
                                    height: "35px",
                                    borderRadius: "50%",
                                    minWidth: "15px",
                                    boxShadow: "none",
                                  }}
                                  onClick={() =>
                                    handleDeletarParametro(
                                      i,
                                      parametroPrecificacaoListVenda,
                                      "venda"
                                    )
                                  }
                                >
                                  <i
                                    className="ph-bold ph-trash"
                                    style={{ color: "#FFFFFF", fontSize: 15 }}
                                  ></i>
                                </Button>
                              </Grid>
                              {i ===
                              parametroPrecificacaoListVenda.length - 1 ? (
                                <Grid
                                  item
                                  xs={0.5}
                                  className="d-flex align-items-center mt-2"
                                >
                                  <Button
                                    variant="contained"
                                    size="small"
                                    sx={{
                                      height: "35px",
                                      borderRadius: "50%",
                                      minWidth: "15px",
                                      boxShadow: "none",
                                    }}
                                    onClick={() =>
                                      onClickAddItem(
                                        setParametroPrecificacaoListVenda
                                      )
                                    }
                                  >
                                    <i
                                      className="ph-bold ph-plus"
                                      style={{ color: "#fff", fontSize: 15 }}
                                    ></i>
                                  </Button>
                                </Grid>
                              ) : null}
                            </Grid>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Grid>
        </CardContent>
      </DialogContent>
      <DialogActions className="px-4">
        <Grid item xs={7} className="d-flex mb-4">
          <Box className="d-flex flex-column align-items-start me-4">
            <CardHeader title="variáveis disponíveis" />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {palavrasChaves
                .slice(0, showAllVariables ? palavrasChaves?.length : 3)
                .map((variavel, index) => (
                  <Button
                    id={`var-${index - 1}`}
                    key={variavel.value}
                    variant="outlined"
                    onClick={() => inserirVariavelNaExpressao(variavel.label)}
                    size="small"
                    sx={VAR_BUTTON_STYLES}
                  >
                    {variavel.label}
                  </Button>
                ))}
              {palavrasChaves.length > 4 && (
                <Button
                  variant="outlined"
                  onClick={() => setShowAllVariables(!showAllVariables)}
                  size="small"
                  color="primary"
                  sx={TOGGLE_BUTTON_STYLES}
                >
                  {showAllVariables ? "Ver menos..." : "Ver mais..."}
                </Button>
              )}
            </Box>
          </Box>
          <Box className="d-flex flex-column align-items-start">
            <CardHeader title="Operações disponíveis" />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {operacoesChaves.map((variavel, index) => (
                <IconButton
                  id={`var-${index - 1}`}
                  key={variavel.value}
                  size="small"
                  onClick={() => inserirVariavelNaExpressao(variavel.value)}
                  sx={{
                    borderRadius: "16px",
                    textTransform: "none",
                    fontSize: "0.8rem",
                    padding: "4px 12px",
                    border: "1px solid #ddd",
                    color: "#555",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                      borderColor: "#ccc",
                    },
                  }}
                >
                  {variavel.icon.startsWith("ph") ? (
                    <i className={`${variavel.icon}`} />
                  ) : (
                    variavel.icon
                  )}
                </IconButton>
              ))}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={5} className="d-flex gap-3 justify-content-end">
          <Button color="padrao" variant="contained" onClick={() => onClose()}>
            <i
              className="ph-fill ph-x-circle"
              style={{ fontSize: 22, marginRight: 10 }}
            ></i>
            Fechar
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => onSubmit()}
          >
            <i
              className="ph-fill ph-check-circle"
              style={{ fontSize: 22, marginRight: 10 }}
            ></i>
            Salvar
          </Button>
        </Grid>
      </DialogActions>
      <Loading loading={loading} />
    </Dialog>
  );
}
